import React from 'react';
import FormSlider from './FormSlider';
import { CreateContactData, Address, Email, Phone } from '../types/contact';

interface ContactFormSliderProps {
  workspaceId: string;
  contactId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  onErrorChange?: (error: string) => void;
}

interface FormData {
  firstName: string;
  middleName: string;
  lastName: string;
  prefix: string;
  suffix: string;
  displayName: string;
  contactType: string[];
  emails: Email[];
  phones: Phone[];
  addresses: Address[];
  doNotCall: boolean;
  doNotEmail: boolean;
  doNotSMS: boolean;
  allowLogin: boolean;
  country: string;
  state: string;
  internalNotes: string;
}

const initialFormData: FormData = {
  firstName: '',
  middleName: '',
  lastName: '',
  prefix: '',
  suffix: '',
  displayName: '',
  contactType: [],
  emails: [{ type: 'Work', email: '', htmlSupported: true, marketing: false, isInactive: false, isPrimary: true }],
  phones: [{ type: 'Mobile', number: '', isInactive: false, isPrimary: true, smsOptIn: false }],
  addresses: [],
  doNotCall: false,
  doNotEmail: false,
  doNotSMS: false,
  allowLogin: false,
  country: '',
  state: '',
  internalNotes: ''
};

const contactTypes = [
  'End-User',
  'Broker',
  'Agent',
  'Investor',
  'Partner',
  'Vendor',
  'Other'
];

const emailTypes = ['Work', 'Personal', 'Other'];
const phoneTypes = ['Mobile', 'Work', 'Home', 'Other'];
const addressTypes = ['Home', 'Work', 'Mailing', 'Other'];

const ContactFormSlider: React.FC<ContactFormSliderProps> = ({
  workspaceId,
  contactId,
  onSuccess,
  onCancel,
  onErrorChange
}) => {
  const validateForm = (data: FormData): string | null => {
    if (!data.firstName.trim()) {
      return 'First name is required';
    }
    if (!data.lastName.trim()) {
      return 'Last name is required';
    }
    if (data.emails.length === 0 || !data.emails[0].email.trim()) {
      return 'At least one email is required';
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of data.emails) {
      if (email.email && !emailRegex.test(email.email)) {
        return `Invalid email format: ${email.email}`;
      }
    }
    return null;
  };

  const transformSubmitData = (data: FormData): CreateContactData => {
    const payload: any = {
      firstName: data.firstName,
      middleName: data.middleName || undefined,
      lastName: data.lastName,
      prefix: data.prefix || undefined,
      suffix: data.suffix || undefined,
      displayName: data.displayName || undefined,
      contactType: data.contactType,
      emails: data.emails.filter(e => e.email.trim()),
      phones: data.phones.filter(p => p.number.trim()),
      addresses: data.addresses,
      doNotCall: data.doNotCall,
      doNotEmail: data.doNotEmail,
      doNotSMS: data.doNotSMS,
      allowLogin: data.allowLogin,
      internalNotes: data.internalNotes || undefined
    };

    if (data.country || data.state) {
      payload.geography = {
        country: data.country || undefined,
        state: data.state || undefined
      };
    }

    return payload;
  };

  const transformFetchData = (data: any): FormData => {
    // Handle API response which wraps contact in { contact: ... }
    const contact = data.contact || data;

    return {
      firstName: contact.firstName || '',
      middleName: contact.middleName || '',
      lastName: contact.lastName || '',
      prefix: contact.prefix || '',
      suffix: contact.suffix || '',
      displayName: contact.displayName || '',
      contactType: contact.contactType || [],
      emails: contact.emails && contact.emails.length > 0 ? contact.emails : initialFormData.emails,
      phones: contact.phones && contact.phones.length > 0 ? contact.phones : initialFormData.phones,
      addresses: contact.addresses || [],
      doNotCall: contact.doNotCall || false,
      doNotEmail: contact.doNotEmail || false,
      doNotSMS: contact.doNotSMS || false,
      allowLogin: contact.allowLogin || false,
      country: contact.geography?.country || '',
      state: contact.geography?.state || '',
      internalNotes: contact.internalNotes || ''
    };
  };

  return (
    <FormSlider<FormData>
      workspaceId={workspaceId}
      itemId={contactId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/contacts`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformSubmitData={transformSubmitData}
      transformFetchData={transformFetchData}
      formId="contact-form"
      onErrorChange={onErrorChange}
    >
      {({ formData, handleInputChange, handleFieldChange, loading, error }) => {
        // Custom handler for name fields that auto-populates displayName
        const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const { name, value } = e.target;

          // Update the field
          handleInputChange(e);

          // Auto-populate displayName if it hasn't been manually edited
          if (['prefix', 'firstName', 'middleName', 'lastName', 'suffix'].includes(name)) {
            // Calculate what displayName should be based on current values
            const updatedData = { ...formData, [name]: value };
            const parts = [
              updatedData.firstName,
              updatedData.middleName,
              updatedData.lastName
            ].filter(Boolean);

            const autoDisplayName = parts.join(' ');

            // Only auto-update if displayName is empty or matches the auto-generated pattern
            const currentParts = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean);
            const currentAutoDisplayName = currentParts.join(' ');

            if (!formData.displayName || formData.displayName === currentAutoDisplayName) {
              handleFieldChange('displayName', autoDisplayName);
            }
          }
        };

        return (
        <div className="drawer-form-content">
          {/* Personal Information */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">Personal Information</h3>

            <div className="drawer-form-row">
              <div className="drawer-form-group" style={{ width: '100px' }}>
                <label className="drawer-form-label">Prefix</label>
                <input
                  type="text"
                  name="prefix"
                  value={formData.prefix}
                  onChange={handleNameChange}
                  className="drawer-form-input"
                  placeholder="Mr."
                />
              </div>
              <div className="drawer-form-group" style={{ flex: 1 }}>
                <label className="drawer-form-label">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleNameChange}
                  className="drawer-form-input"
                  required
                />
              </div>
            </div>

            <div className="drawer-form-row">
              <div className="drawer-form-group">
                <label className="drawer-form-label">Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleNameChange}
                  className="drawer-form-input"
                />
              </div>
              <div className="drawer-form-group">
                <label className="drawer-form-label">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleNameChange}
                  className="drawer-form-input"
                  required
                />
              </div>
            </div>

            <div className="drawer-form-row">
              <div className="drawer-form-group" style={{ width: '100px' }}>
                <label className="drawer-form-label">Suffix</label>
                <input
                  type="text"
                  name="suffix"
                  value={formData.suffix}
                  onChange={handleNameChange}
                  className="drawer-form-input"
                  placeholder="Jr."
                />
              </div>
              <div className="drawer-form-group" style={{ flex: 1 }}>
                <label className="drawer-form-label">Display Name</label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  className="drawer-form-input"
                  placeholder="Auto-generated if empty"
                />
              </div>
            </div>
          </div>

          {/* Contact Type */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">
              Contact Type
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {contactTypes.map((type) => (
                <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.contactType.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.contactType, type]
                        : formData.contactType.filter(t => t !== type);
                      handleFieldChange('contactType', newTypes);
                    }}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Email Addresses */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">
              Email Addresses <span className="required">*</span>
            </h3>
            {formData.emails.map((email, index) => (
              <div key={index} className="drawer-form-array-item">
                <div className="drawer-form-row">
                  <div className="drawer-form-group" style={{ width: '120px' }}>
                    <label className="drawer-form-label">Type</label>
                    <select
                      value={email.type}
                      onChange={(e) => {
                        const newEmails = [...formData.emails];
                        newEmails[index].type = e.target.value;
                        handleFieldChange('emails', newEmails);
                      }}
                      className="drawer-form-input"
                    >
                      {emailTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="drawer-form-group" style={{ flex: 1 }}>
                    <label className="drawer-form-label">Email</label>
                    <input
                      type="email"
                      value={email.email}
                      onChange={(e) => {
                        const newEmails = [...formData.emails];
                        newEmails[index].email = e.target.value;
                        handleFieldChange('emails', newEmails);
                      }}
                      className="drawer-form-input"
                      required={index === 0}
                    />
                  </div>
                  <div style={{ paddingTop: '28px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={email.isPrimary}
                        onChange={(e) => {
                          const newEmails = formData.emails.map((em, i) => ({
                            ...em,
                            isPrimary: i === index ? e.target.checked : false
                          }));
                          handleFieldChange('emails', newEmails);
                        }}
                      />
                      Primary
                    </label>
                  </div>
                </div>
                {formData.emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      handleFieldChange('emails', formData.emails.filter((_, i) => i !== index));
                    }}
                    className="drawer-form-remove-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                handleFieldChange('emails', [
                  ...formData.emails,
                  { type: 'Work', email: '', htmlSupported: true, marketing: false, isInactive: false, isPrimary: false }
                ]);
              }}
              className="drawer-form-add-btn"
            >
              + Add Email
            </button>
          </div>

          {/* Phone Numbers */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">Phone Numbers</h3>
            {formData.phones.map((phone, index) => (
              <div key={index} className="drawer-form-array-item">
                <div className="drawer-form-row">
                  <div className="drawer-form-group" style={{ width: '120px' }}>
                    <label className="drawer-form-label">Type</label>
                    <select
                      value={phone.type}
                      onChange={(e) => {
                        const newPhones = [...formData.phones];
                        newPhones[index].type = e.target.value;
                        handleFieldChange('phones', newPhones);
                      }}
                      className="drawer-form-input"
                    >
                      {phoneTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="drawer-form-group" style={{ flex: 1 }}>
                    <label className="drawer-form-label">Number</label>
                    <input
                      type="tel"
                      value={phone.number}
                      onChange={(e) => {
                        const newPhones = [...formData.phones];
                        newPhones[index].number = e.target.value;
                        handleFieldChange('phones', newPhones);
                      }}
                      className="drawer-form-input"
                    />
                  </div>
                  <div style={{ paddingTop: '28px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={phone.isPrimary}
                        onChange={(e) => {
                          const newPhones = formData.phones.map((ph, i) => ({
                            ...ph,
                            isPrimary: i === index ? e.target.checked : false
                          }));
                          handleFieldChange('phones', newPhones);
                        }}
                      />
                      Primary
                    </label>
                  </div>
                </div>
                {formData.phones.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      handleFieldChange('phones', formData.phones.filter((_, i) => i !== index));
                    }}
                    className="drawer-form-remove-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                handleFieldChange('phones', [
                  ...formData.phones,
                  { type: 'Mobile', number: '', isInactive: false, isPrimary: false, smsOptIn: false }
                ]);
              }}
              className="drawer-form-add-btn"
            >
              + Add Phone
            </button>
          </div>

          {/* Geography */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">Geography</h3>
            <div className="drawer-form-row">
              <div className="drawer-form-group">
                <label className="drawer-form-label">Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="drawer-form-input"
                  placeholder="e.g., Canada"
                />
              </div>
              <div className="drawer-form-group">
                <label className="drawer-form-label">State/Province</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="drawer-form-input"
                  placeholder="e.g., Ontario"
                />
              </div>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">Communication Preferences</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="doNotCall"
                  checked={formData.doNotCall}
                  onChange={handleInputChange}
                />
                <span>Do Not Call</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="doNotEmail"
                  checked={formData.doNotEmail}
                  onChange={handleInputChange}
                />
                <span>Do Not Email</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="doNotSMS"
                  checked={formData.doNotSMS}
                  onChange={handleInputChange}
                />
                <span>Do Not SMS</span>
              </label>
            </div>
          </div>

          {/* Portal Access */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">Purchaser Portal</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                name="allowLogin"
                checked={formData.allowLogin}
                onChange={handleInputChange}
              />
              <span>Allow portal access</span>
            </label>
          </div>

          {/* Internal Notes */}
          <div className="drawer-form-section">
            <h3 className="drawer-form-section-title">Internal Notes</h3>
            <div className="drawer-form-group">
              <textarea
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleInputChange}
                className="drawer-form-textarea"
                rows={4}
                placeholder="Private notes about this contact..."
              />
            </div>
          </div>
        </div>
        );
      }}
    </FormSlider>
  );
};

export default ContactFormSlider;
